import {
  ChecklistItemModel,
  ChecklistItemAnswerModel,
  ChecklistSectionModel,
  InspectionTemplateModel,
} from './inspectionTemplateModel';
import { sortByFractionalOrder } from 'helpers/sort';
import { AnswerType } from 'shared/domain/checklistAnswer/types/answerType';
import { NumericUnit } from 'shared/domain/checklistAnswer/types/numericUnit';
import { ComplianceAnswerOption } from 'shared/domain/compliance/complianceAnswerOption';
import { getActiveChecklistAnswers } from './checklistAnswerGuards';
import {
  EMPTY_GENERAL_NORM_CONFIG,
  GeneralNormConfig,
  normalizeGeneralNormConfig,
} from './generalNormConfig';

export interface TemplateDraftComplianceOptionLabel {
  option: ComplianceAnswerOption;
  label: string;
}

export interface TemplateDraftSelectionOption {
  _id: string;
  label: string;
}

interface TemplateDraftChecklistAnswerBase {
  _id?: string;
  uniqueKeyId: string;
  type: AnswerType;
  label: string;
  isRequired: boolean;
}

export interface TemplateDraftComplianceChecklistAnswer
  extends TemplateDraftChecklistAnswerBase {
  type: AnswerType.Compliance;
  optionLabels: TemplateDraftComplianceOptionLabel[];
}

export interface TemplateDraftNumericChecklistAnswer
  extends TemplateDraftChecklistAnswerBase {
  type: AnswerType.Numeric;
  unit?: NumericUnit | string;
  normMin?: number;
  normMax?: number;
  rangeMin?: number;
  rangeMax?: number;
  step?: number;
}

export interface TemplateDraftTextChecklistAnswer
  extends TemplateDraftChecklistAnswerBase {
  type: AnswerType.Text;
}

export interface TemplateDraftOptionSelectionChecklistAnswer
  extends TemplateDraftChecklistAnswerBase {
  type: AnswerType.OptionSelection;
  options: TemplateDraftSelectionOption[];
  isMultiselect: boolean;
}

export type TemplateDraftChecklistAnswer =
  | TemplateDraftComplianceChecklistAnswer
  | TemplateDraftNumericChecklistAnswer
  | TemplateDraftTextChecklistAnswer
  | TemplateDraftOptionSelectionChecklistAnswer;

export interface TemplateDraftChecklistItem {
  _id?: string;
  uniqueKeyId: string;
  summary: string;
  description: string;
  parentSectionId: string | null;
  order: string;
  answers: TemplateDraftChecklistAnswer[];
}

export interface TemplateDraftSection {
  _id?: string;
  uniqueKeyId: string;
  title: string;
  description: string;
  parentSectionId: string | null;
  order: string;
}

export interface TemplateDraftDefaultValues {
  _id: string;
  summary: string;
  generalNorm: {
    description: string;
    documents: unknown[];
  };
  generalNormConfig: GeneralNormConfig;
  process: string | null;
  checklist: TemplateDraftChecklistItem[];
  sections: TemplateDraftSection[];
}

const EMPTY_CHECKLIST_ITEM: TemplateDraftChecklistItem = {
  uniqueKeyId: '',
  summary: '',
  description: '',
  parentSectionId: null,
  order: '',
  answers: [],
};

const EMPTY_SECTION: TemplateDraftSection = {
  uniqueKeyId: '',
  title: '',
  description: '',
  parentSectionId: null,
  order: '',
};

function mongoObjectId(): string {
  const timestamp = ((new Date().getTime() / 1000) | 0).toString(16);

  return (
    timestamp +
    'xxxxxxxxxxxxxxxx'
      .replace(/[x]/g, () => {
        return ((Math.random() * 16) | 0).toString(16);
      })
      .toLowerCase()
  );
}

export function generateOrder(existingItems: { order: string }[]): string {
  if (existingItems.length === 0) {
    return 'a0';
  }

  const sortedItems = [...existingItems].sort(sortByFractionalOrder);
  const lastOrder = sortedItems[sortedItems.length - 1].order;
  const lastChar = lastOrder.charCodeAt(lastOrder.length - 1);

  if (lastChar < 122) {
    return lastOrder.slice(0, -1) + String.fromCharCode(lastChar + 1);
  }

  return lastOrder + 'a';
}

export function calculateOrderBetween(beforeOrder: string, afterOrder: string): string {
  const minLength = Math.min(beforeOrder.length, afterOrder.length);

  for (let i = 0; i < minLength; i++) {
    const beforeChar = beforeOrder.charCodeAt(i);
    const afterChar = afterOrder.charCodeAt(i);

    if (afterChar - beforeChar > 1) {
      return beforeOrder.slice(0, i) + String.fromCharCode(Math.floor((beforeChar + afterChar) / 2));
    }

    if (afterChar - beforeChar === 1) {
      return beforeOrder + 'n';
    }
  }

  if (beforeOrder.length < afterOrder.length) {
    return beforeOrder + 'n';
  }

  return beforeOrder.slice(0, -1) +
    String.fromCharCode(beforeOrder.charCodeAt(beforeOrder.length - 1) + 1);
}

export function calculateOrderBefore(afterOrder: string): string {
  const firstChar = afterOrder.charCodeAt(0);

  if (firstChar > 98) {
    return String.fromCharCode(Math.floor((97 + firstChar) / 2));
  }

  if (afterOrder.length === 1) {
    return 'A' + afterOrder;
  }

  const prefix = afterOrder.slice(0, -1);
  const lastChar = afterOrder.charCodeAt(afterOrder.length - 1);

  if (lastChar > 98) {
    return prefix + String.fromCharCode(Math.floor((97 + lastChar) / 2));
  }

  return 'A' + afterOrder;
}

export function createTemplateDraftChecklistItem(
  parentSectionId: string | null = null,
  existingItems: Array<Pick<TemplateDraftChecklistItem, 'parentSectionId' | 'order'>> = []
): TemplateDraftChecklistItem {
  const itemsInParent = existingItems.filter(
    (item) => item.parentSectionId === parentSectionId
  );

  return {
    ...EMPTY_CHECKLIST_ITEM,
    uniqueKeyId: mongoObjectId(),
    parentSectionId,
    order: generateOrder(itemsInParent),
  };
}

export function createTemplateDraftSection(
  parentSectionId: string | null = null,
  existingSections: Array<Pick<TemplateDraftSection, 'parentSectionId' | 'order'>> = []
): TemplateDraftSection {
  const sectionsInParent = existingSections.filter(
    (section) => section.parentSectionId === parentSectionId
  );

  return {
    ...EMPTY_SECTION,
    uniqueKeyId: mongoObjectId(),
    parentSectionId,
    order: generateOrder(sectionsInParent),
  };
}

export function createEmptyTemplateDraftDefaults(): TemplateDraftDefaultValues {
  const initialItem = createTemplateDraftChecklistItem(null, []);

  return {
    _id: '',
    summary: '',
    generalNorm: {
      description: '',
      documents: [],
    },
    generalNormConfig: { ...EMPTY_GENERAL_NORM_CONFIG },
    process: null,
    checklist: [initialItem],
    sections: [],
  };
}

function toTemplateDraftChecklistAnswer(
  answer: ChecklistItemAnswerModel
): TemplateDraftChecklistAnswer {
  const base = {
    _id: answer._id,
    uniqueKeyId: answer._id,
    label: answer.label,
    isRequired: answer.isRequired,
  };

  switch (answer.type) {
    case AnswerType.Compliance:
      return {
        ...base,
        type: AnswerType.Compliance,
        optionLabels: answer.optionLabels.map((ol) => ({
          option: ol.option,
          label: ol.label,
        })),
      };
    case AnswerType.Numeric:
      return {
        ...base,
        type: AnswerType.Numeric,
        unit: answer.unit,
        normMin: answer.normMin,
        normMax: answer.normMax,
        rangeMin: answer.rangeMin,
        rangeMax: answer.rangeMax,
        step: answer.step,
      };
    case AnswerType.Text:
      return {
        ...base,
        type: AnswerType.Text,
      };
    case AnswerType.OptionSelection:
      return {
        ...base,
        type: AnswerType.OptionSelection,
        options: answer.options.map((opt) => ({
          _id: opt._id,
          label: opt.label,
        })),
        isMultiselect: answer.isMultiselect,
      };
  }
}

function toTemplateDraftChecklistItem(
  checklistItem: ChecklistItemModel
): TemplateDraftChecklistItem {
  return {
    _id: checklistItem._id,
    uniqueKeyId: checklistItem._id,
    summary: checklistItem.summary,
    description: checklistItem.norm.description,
    parentSectionId: checklistItem.parentSectionId || null,
    order: checklistItem.order || '',
    answers: getActiveChecklistAnswers(checklistItem.answers).map(
      toTemplateDraftChecklistAnswer
    ),
  };
}

function toTemplateDraftSection(
  section: ChecklistSectionModel
): TemplateDraftSection {
  return {
    _id: section._id,
    uniqueKeyId: section._id,
    title: section.title,
    description: section.description,
    parentSectionId: section.parentSectionId || null,
    order: section.order || '',
  };
}

export function toTemplateDraftDefaults(
  template: InspectionTemplateModel
): TemplateDraftDefaultValues {
  const sections = (template.sections || [])
    .filter((section) => !section.deleted)
    .map(toTemplateDraftSection);

  return {
    _id: template._id,
    process: template.process,
    summary: template.summary,
    generalNorm: {
      description: template.generalNorm.description,
      documents: [],
    },
    generalNormConfig: normalizeGeneralNormConfig(template.generalNormConfig),
    checklist: template.checklist.reduce<TemplateDraftChecklistItem[]>(
      (checklist, checklistItem) => {
        if (!checklistItem.deleted) {
          checklist.push(toTemplateDraftChecklistItem(checklistItem));
        }

        return checklist;
      },
      []
    ),
    sections,
  };
}

export function deepCopy<T>(object: T): T {
  return JSON.parse(JSON.stringify(object));
}